import GridActions from '@/components/Base/GridActions/actions'
import { LoadingCard } from '@/components/Base/LoadingCard/LoadingCard'
import { NoDataToDisplayCard } from '@/components/Base/NoDataToDisplayCard/NoDataToDisplayCard'
import { ValueCard } from '@/components/Base/ValueCard/ValueCard'
import { GFRDashboardNavigation } from '@/components/GFR/GFRDashboardNavigation'
import { GFRSummaryChart } from '@/components/GFR/SummaryReport/GFRSummaryChart'
import { DATE_FORMAT } from '@/constants/common.constant'
import { AxiomReportType } from '@/enums/axiom-report.enum'
import { useAppDispatch } from '@/hooks/use-app-dispatch'
import { useAppSelector } from '@/hooks/use-app-selector'
import {
  IGfrRiskHistoryDTO,
  IGfrRiskOverView,
} from '@/interfaces/gfrReport.interface'
import { ReportDashboardLayout } from '@/Layouts/ReportDashboardLayout'
import { GFPAFactorContributionPage } from '@/pages/GlobalFactorPerformanceAttribution/GFPAFactorContributionPage/GFPAFactorContributionPage'
import {
  getColumnDefByReportType,
  getRiskOverview,
} from '@/services/axiomReport.service'
import {
  clearGFRSummary,
  fetchGFRSummaryAsync,
} from '@/states/axiomReport.slice'
import { getColumnDetail } from '@/utils/grid-utils/columnUtils'
import { formatCellValue } from '@/utils/tableGridUtils'
import { formatNumber } from '@/utils/utils'
import { AgGridReact } from '@ag-grid-community/react'
import { MoreVert } from '@mui/icons-material'
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

export const GFRDashboardPage: React.FC = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const selectedReport = useAppSelector((s) => s.axiomReport.selectedReport)
  const gfrSummary = useAppSelector((s) => s.axiomReport.gfrReport.summary)
  const showLoader = useAppSelector((s) => s.axiomReport.showLoader)
  const [loaderCard, setLoaderCard] = useState<boolean>(false)
  const [rowData, setRowData] = useState<IGfrRiskHistoryDTO[]>([])
  const [riskOverview, setRiskOverView] = useState<IGfrRiskOverView>(
    {} as IGfrRiskOverView,
  )
  const [gridReference, setGridReference] = useState<AgGridReact>()

  const loader = () => {
    return (
      <Box
        display='grid'
        height='100%'
        alignItems='center'
        justifyContent='center'
        role='loading'
      >
        <CircularProgress color='primary' />
      </Box>
    )
  }

  const fetchRiskOverview = async () => {
    try {
      setLoaderCard(true)
      const res = await getRiskOverview(selectedReport.reportId)
      setRiskOverView(res)
    } catch (err) {
      //setError(err.message || 'An error occurred while fetching the total return.');
    } finally {
      setLoaderCard(false)
    }
  }

  const fetchColumnDefByReportType = async () => {
    try {
      const colRes = await getColumnDefByReportType(AxiomReportType.GFR)
      sessionStorage.setItem(AxiomReportType.GFR, JSON.stringify(colRes))
    } catch (err) {}
  }

  useEffect(() => {
    fetchRiskOverview()
    const isStoredInSession = sessionStorage.getItem(AxiomReportType.GFR)
    if (!isStoredInSession) {
      fetchColumnDefByReportType()
    }

    if (
      Object.keys(selectedReport).length &&
      selectedReport.reportType === AxiomReportType.GFR
    ) {
      dispatch(
        fetchGFRSummaryAsync(
          selectedReport.reportId,
          dayjs(selectedReport.endDate)
            .subtract(90, 'day')
            .format(DATE_FORMAT.API),
          dayjs(selectedReport.endDate).format(DATE_FORMAT.API),
        ),
      )
    } else {
      navigate('/')
    }

    return () => {
      dispatch(clearGFRSummary())
    }
  }, [selectedReport])

  useEffect(() => {
    gfrSummary && gfrSummary.length && setRowData(gfrSummary)
  }, [gfrSummary])

  // Daily Contribution Menu
  const [summaryMenuEl, setSummaryMenuEl] = React.useState<null | HTMLElement>(
    null,
  )
  const summaryMenu = Boolean(summaryMenuEl)
  const openSummaryMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setSummaryMenuEl(event.currentTarget)
  }
  const closeSummaryMenu = () => {
    setSummaryMenuEl(null)
  }

  return (
    <ReportDashboardLayout
      selectedReport={selectedReport}
      navigation={<GFRDashboardNavigation />}
    >
      <Box display='grid' gap={4}>
        <Card sx={{ boxSizing: 'border-box' }}>
          <CardHeader title={<Typography variant='h3'>Summary</Typography>} />
          <CardContent
            sx={{ boxSizing: 'border-box', overflowY: 'auto', px: 4, mx: 4 }}
          >
            {!loaderCard ? (
              <Stack
                direction='row'
                gap={9}
                alignItems='center'
                justifyContent='space-between'
              >
                {Object.keys(riskOverview)?.map(
                  (key: keyof IGfrRiskOverView | string) => {
                    const colDetail = getColumnDetail(key, AxiomReportType.GFR)
                    const value = riskOverview[key as keyof IGfrRiskOverView]
                    const label = colDetail?.header
                    const displayValue = formatCellValue(
                      formatNumber(value, true, 2, 2),
                      colDetail?.format,
                    )
                    return (
                      <ValueCard
                        key={key}
                        label={label}
                        value={value}
                        displayValue={displayValue}
                        reportType={selectedReport.reportType}
                      />
                    )
                  },
                )}
              </Stack>
            ) : (
              loader()
            )}
          </CardContent>
        </Card>

        <Card sx={{ boxSizing: 'border-box', height: '100%' }}>
          <CardHeader
            title={
              <Stack
                direction='row'
                alignItems='center'
                justifyContent='flex-start'
                gap={4}
              >
                <Typography variant='h3'>Factor Contribution</Typography>
                <Divider orientation='vertical' variant='middle' flexItem />
                <Typography
                  variant='h5'
                  color='text.secondary'
                  fontWeight='fontWeightBold'
                >
                  All Factors
                </Typography>
              </Stack>
            }
            titleTypographyProps={{
              component: 'div',
              display: 'block',
            }}
            action={
              <Stack direction='row'>
                <GridActions gridApiRef={gridReference} />
              </Stack>
            }
            sx={{
              pb: 0,
            }}
          />
          <CardContent
            sx={{
              boxSizing: 'border-box',
              height: '100%',
              pt: 0,
            }}
          >
            {showLoader ? (
              <Box
                display='grid'
                height='100dvh'
                alignItems='center'
                justifyContent='center'
                role='loading'
              >
                <CircularProgress color='primary' />
              </Box>
            ) : (
              <GFPAFactorContributionPage setGridReference={setGridReference} />
            )}
          </CardContent>
        </Card>

        <Card sx={{ boxSizing: 'border-box' }}>
          <CardHeader
            title={<Typography variant='h3'>90-Day Risk History</Typography>}
            action={
              <>
                <IconButton
                  onClick={openSummaryMenu}
                  aria-controls={summaryMenu ? 'summary-menu' : undefined}
                >
                  <MoreVert fontSize='inherit' />
                </IconButton>
                <Menu
                  id='basic-menu'
                  anchorEl={summaryMenuEl}
                  open={summaryMenu}
                  onClose={closeSummaryMenu}
                  MenuListProps={{
                    'aria-labelledby': 'summary-menu',
                    style: { minWidth: '15rem' },
                  }}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                >
                  <MenuItem
                    onClick={() =>
                      navigate(
                        `/reports/${selectedReport.reportId}/gfr/summary`,
                      )
                    }
                  >
                    View Details
                  </MenuItem>
                </Menu>
              </>
            }
          />
          <CardContent
            sx={{
              boxSizing: 'border-box',
              height: 'calc(100% - 4rem)',
            }}
          >
            <Box
              display='grid'
              height='15rem'
              alignItems='center'
              justifyContent={
                !showLoader && rowData.length ? 'stretch' : 'center'
              }
              role='loading'
            >
              {showLoader ? (
                <LoadingCard />
              ) : rowData.length ? (
                <GFRSummaryChart chartData={rowData} />
              ) : (
                <NoDataToDisplayCard />
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>
    </ReportDashboardLayout>
  )
}
